import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchAgents } from '../lib/agentApi';

const storageKey = (orgId) => `doctree-agent:${orgId}`;

/**
 * Agents for the current workspace plus the selected one.
 *
 * The AI Agent pages are flat routes (no :agentId segment), so the selection is
 * held here and persisted per workspace — switching orgs must not carry an agent
 * id that belongs to a different tenant.
 */
export function useAgents() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;

  const [agents, setAgents] = useState([]);
  const [meta, setMeta] = useState({ limit: 0, used: 0, canCreate: false, addonEnabled: false });
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(Boolean(orgId));
  const [error, setError] = useState(null);

  const selectAgent = useCallback(
    (agentId) => {
      setSelectedId(agentId);
      if (orgId && agentId) {
        try {
          localStorage.setItem(storageKey(orgId), agentId);
        } catch {
          /* private mode — selection stays in memory for this session */
        }
      }
    },
    [orgId]
  );

  const refresh = useCallback(async () => {
    if (!orgId) {
      setAgents([]);
      setSelectedId(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAgents(orgId);
      const list = data.agents || [];
      setAgents(list);
      setMeta({
        limit: data.limit,
        used: data.used,
        canCreate: data.can_create,
        addonEnabled: data.addon_enabled,
        planId: data.plan_id,
      });

      let stored = null;
      try {
        stored = localStorage.getItem(storageKey(orgId));
      } catch {
        stored = null;
      }
      const exists = list.some((a) => a.id === stored);
      setSelectedId(exists ? stored : list[0]?.id || null);
      return data;
    } catch (err) {
      setError(err);
      setAgents([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    orgId,
    agents,
    meta,
    loading,
    error,
    refresh,
    selectedId,
    selectAgent,
    selectedAgent: agents.find((a) => a.id === selectedId) || null,
  };
}
